/**
 * Project service - API calls for the Project & Funding module
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  Project,
  ProjectListFilters,
  ProjectListResponse,
  Milestone,
  RecommendedInstrument,
} from '../types/project';

export async function fetchProjects(
  filters?: ProjectListFilters
): Promise<ApiResponse<ProjectListResponse>> {
  try {
    const response = await api.get('/projects', { params: filters });
    const payload = response.data;

    if (Array.isArray(payload)) {
      return {
        data: {
          projects: payload,
          total: payload.length,
          page: 1,
          size: payload.length,
          pages: 1,
        },
      };
    }

    return {
      data: {
        projects: payload?.projects || payload?.items || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 20,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load projects' };
  }
}

export async function fetchProject(id: number): Promise<ApiResponse<Project>> {
  try {
    const response = await api.get(`/projects/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load project' };
  }
}

export async function createProject(
  data: Partial<Project>
): Promise<ApiResponse<Project>> {
  try {
    const response = await api.post('/projects', data);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to create project' };
  }
}

export async function updateProject(
  id: number,
  data: Partial<Project>
): Promise<ApiResponse<Project>> {
  try {
    const response = await api.patch(`/projects/${id}`, data);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to update project' };
  }
}

export async function deleteProject(
  id: number
): Promise<ApiResponse<{ message: string }>> {
  try {
    const response = await api.delete(`/projects/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to delete project' };
  }
}

export async function addMilestone(
  id: number,
  milestone: Milestone
): Promise<ApiResponse<Project>> {
  try {
    const response = await api.post(`/projects/${id}/milestones`, milestone);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to add milestone' };
  }
}

export async function updateMilestone(
  id: number,
  idx: number,
  patch: Partial<Milestone>
): Promise<ApiResponse<Project>> {
  try {
    const response = await api.patch(`/projects/${id}/milestones/${idx}`, patch);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to update milestone' };
  }
}

export async function getRecommendedInstruments(
  id: number
): Promise<ApiResponse<RecommendedInstrument[]>> {
  try {
    const response = await api.get(`/projects/${id}/recommend-instruments`);
    const payload = response.data;
    return { data: Array.isArray(payload) ? payload : payload?.recommendations || [] };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load recommendations' };
  }
}

const projectService = {
  fetchProjects,
  fetchProject,
  createProject,
  updateProject,
  deleteProject,
  addMilestone,
  updateMilestone,
  getRecommendedInstruments,
};

export default projectService;
